/**
 * The delivery log: what went out, what is waiting, and what went wrong.
 *
 * The screen that makes email answerable. Before the outbox, "did the reviewer
 * get told" had no answer anywhere in the product - a send happened inline and
 * a failure became a line in a log file nobody reads.
 *
 * No message bodies here, deliberately. The log carries the subject and the
 * error, which is what answers the question; the rendered body of a password
 * reset is a live link, and a delivery log is not a place to keep those.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { EmailOutboxEntry } from '../../lib/api/client'
import type { Tone } from '../../components/statusTone'
import { Button, EmptyState, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import './email.css'

/** Every status the server can report, and how each should read at a glance. */
const STATUS_TONE: Record<string, Tone> = {
  sent: 'success',
  queued: 'secondary',
  failed: 'danger',
  skipped: 'warning',
}

const STATUS_LABEL: Record<string, string> = {
  sent: 'Sent',
  queued: 'Waiting',
  failed: 'Failed',
  skipped: 'Not sent',
}

const FILTERS: [string, string][] = [
  ['', 'All'],
  ['queued', 'Waiting'],
  ['sent', 'Sent'],
  ['failed', 'Failed'],
  ['skipped', 'Not sent'],
]

export function EmailOutbox() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const log = useQuery({
    queryKey: ['platform', 'email', 'outbox', status, page],
    queryFn: () => api.emailOutbox({ status: status || undefined, page }),
    // The worker runs every 30 seconds; without this a queued message appears
    // stuck until the administrator reloads the page themselves.
    refetchInterval: 15_000,
  })

  const retry = useMutation({
    mutationFn: (id: string) => api.retryEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'email', 'outbox'] })
      toast('Message queued to send again.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (log.isLoading) return <Spinner label="Loading delivery log" />
  if (!log.data) return null

  const counts = log.data.counts ?? {}

  return (
    <section className="tc-email-panel" aria-labelledby="email-outbox-title">
      <header className="tc-email-heading">
        <span className="tc-email-icon" aria-hidden="true">
          <Icon.audit size={20} />
        </span>
        <div>
          <span className="tc-email-eyebrow">Delivery</span>
          <h2 id="email-outbox-title">Delivery log</h2>
          <p>
            Every notification the platform has tried to send. Messages are sent in the background
            and retried automatically; sent messages are removed after seven days.
          </p>
        </div>
        {counts.failed ? (
          <Pill tone="danger">{counts.failed} failed</Pill>
        ) : (
          <Pill tone="success">Nothing failing</Pill>
        )}
      </header>

      <div className="tc-email-body">
        <div className="tc-email-filters" role="group" aria-label="Filter by delivery status">
          {FILTERS.map(([key, label]) => (
            <button
              key={key || 'all'}
              type="button"
              className={`tc-tab ${status === key ? 'is-active' : ''}`}
              aria-pressed={status === key}
              onClick={() => {
                setStatus(key)
                setPage(1)
              }}
            >
              {label}
              {key && counts[key] ? ` (${counts[key]})` : ''}
            </button>
          ))}
        </div>

        {log.data.entries.length === 0 ? (
          <EmptyState
            title="Nothing here yet"
            message={
              status
                ? 'No messages with this status.'
                : 'Notifications appear here as soon as something happens that is worth telling somebody about.'
            }
          />
        ) : (
          <div className="tc-table-wrap">
            <table className="tc-table tc-email-log">
              <thead>
                <tr>
                  <th scope="col">Notification</th>
                  <th scope="col">To</th>
                  <th scope="col">Status</th>
                  <th scope="col">When</th>
                  <th scope="col">
                    <span className="tc-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.data.entries.map((entry) => (
                  <LogRow
                    key={entry.id}
                    entry={entry}
                    retrying={retry.isPending}
                    onRetry={() => retry.mutate(entry.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {log.data.page_count > 1 && (
          <div className="tc-email-actions">
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className="tc-email-note">
              Page {log.data.page} of {log.data.page_count}
            </span>
            <Button
              variant="secondary"
              disabled={page >= log.data.page_count}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

function LogRow({
  entry,
  retrying,
  onRetry,
}: {
  entry: EmailOutboxEntry
  retrying: boolean
  onRetry: () => void
}) {
  const when = entry.sent_at ?? entry.next_attempt_at ?? entry.created_at
  const canRetry = entry.status === 'failed' || entry.status === 'skipped'

  return (
    <tr>
      <td data-label="Notification">
        <strong>{entry.event_label}</strong>
        <span className="tc-email-log-subject">{entry.subject}</span>
        {entry.last_error && (
          /* The mail server's own words. Only site administrators read this
             screen, and they are the ones who have to go and fix the server. */
          <span className="tc-email-log-error">{entry.last_error}</span>
        )}
      </td>
      <td data-label="To">
        {entry.to_email}
        {entry.school_name && <span className="tc-email-log-school">{entry.school_name}</span>}
      </td>
      <td data-label="Status">
        <Pill tone={STATUS_TONE[entry.status] ?? 'muted'}>
          {STATUS_LABEL[entry.status] ?? entry.status}
        </Pill>
        {entry.attempts > 1 && (
          <span className="tc-email-log-attempts">{entry.attempts} attempts</span>
        )}
      </td>
      <td data-label="When">{when ? new Date(when).toLocaleString() : '—'}</td>
      <td data-label="Actions">
        {canRetry && (
          <Button variant="ghost" size="sm" disabled={retrying} onClick={onRetry}>
            Try again
          </Button>
        )}
      </td>
    </tr>
  )
}
